/* eslint-disable camelcase */
import {FC} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {FoodDiningShape} from '@stencil/hotels-cg-reducer/src/details/types';

import useDetailsScrollGa from '@stencil/use-details-scroll-ga';

import {detailsNavigationIds} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';

import * as S from './SingleRestaurentUI.styles';

// Components
import DiningDetailsUI from '../DiningDetailsUI/DiningDetailsUI';
import FoodOfferUI from '../FoodOfferUI/FoodOfferUI';
import NoDiningComponent from '../NoDiningComponent/NoDiningComponent';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {DetailsPageComponents} from '@stencil/omniture-analytics/src/constants/details';

interface SingleRestaurentUIProps {
  header?: string;
  fnd: FoodDiningShape;
  hasKitchen: boolean;
  ambienceRating?;
  tasteRating?;
}

const SingleRestaurentUI: FC<SingleRestaurentUIProps> = ({
  header = 'Food & Dining',
  fnd,
  hasKitchen,
  ambienceRating,
  tasteRating,
}) => {
  const dispatch = useDispatch();
  const {foodWatcherRef} = useDetailsScrollGa();

  const {restaurants, roomServiceText} = fnd;
  const {name, gallery, type, uspDishes} = restaurants[0];
  const {avgMealCost, cuisines, timings, foodType} = restaurants[0]?.details;
  const showNoRating = !ambienceRating && !tasteRating;
  const hasKitchenOnly = showNoRating && hasKitchen;

  const tasteRatingFormatted = tasteRating > 0 && Math.round(tasteRating * 10) / 10;

  const noDiningDetails =
    !avgMealCost?.text && !cuisines?.text && !timings?.text && !foodType?.text;

  const entireCardClickedHandler = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.SingleRestraunt}_Clicked`,
          type: omnitureConfig.ctaType.Card,
          componentName: DetailsPageComponents.SingleRestraunt,
          destination: '',
        },
      }),
    );
  };

  return (
    <>
      <S.SingleRestaurentUI
        id={detailsNavigationIds.foodDining}
        ref={foodWatcherRef}
        onClick={entireCardClickedHandler}
      >
        <S.DiningHeader>{header}</S.DiningHeader>
        <S.SingleFoodWrapper>
          <S.ContentWrap hasKitchenOnly={hasKitchenOnly && noDiningDetails}>
            {name && <S.RestaurantnamePara>{`${name} (${type})`}</S.RestaurantnamePara>}
            <S.FoodRatingWrapper hasKitchenOnly={hasKitchenOnly && noDiningDetails}>
              {!showNoRating && (
                <>
                  {ambienceRating > 0 && (
                    <li>
                      <S.RatingSpan rating={ambienceRating}>
                        <S.RatingNumberSpan>{ambienceRating}</S.RatingNumberSpan>/5
                      </S.RatingSpan>
                      <S.AmbienceSpan>Ambience</S.AmbienceSpan>
                    </li>
                  )}
                  {tasteRating > 0 && (
                    <li>
                      <S.RatingSpan rating={tasteRatingFormatted}>
                        <S.RatingNumberSpan>{tasteRatingFormatted}</S.RatingNumberSpan>/5
                      </S.RatingSpan>
                      <S.AmbienceSpan>Taste</S.AmbienceSpan>
                    </li>
                  )}
                </>
              )}
              {!!hasKitchen && roomServiceText && (
                <li style={{paddingRight: 'unset'}}>
                  <NoDiningComponent roomServiceText={roomServiceText} />
                </li>
              )}
            </S.FoodRatingWrapper>

            <S.OfferContent>
              <DiningDetailsUI
                timings={timings}
                avgMealCost={avgMealCost}
                cuisines={cuisines}
                foodType={foodType}
              />
              {uspDishes && uspDishes.length > 0 && <FoodOfferUI offers={uspDishes} />}
            </S.OfferContent>
          </S.ContentWrap>
        </S.SingleFoodWrapper>
      </S.SingleRestaurentUI>
    </>
  );
};

export default SingleRestaurentUI;
